import { observable, action, makeObservable, computed, runInAction } from "mobx";
import { PROCESS } from "src/stores/TicketStore";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_REALESTATENAME_BY_ID } from "src/api/realestate";
import { GetRealestateNameById, GetRealestateNameByIdVariables } from "src/api/generated/GetRealestateNameById";
import { GET_UNITNAME_BY_ID } from "src/api/realestateUnits";
import { GetUnitNameById, GetUnitNameByIdVariables } from "src/api/generated/GetUnitNameById";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import i18next from "i18next";
import { format } from "date-fns-tz";
import { de, fr, enGB, it } from "date-fns/locale";
import {
    GetAssemblyRealestateUserAndAccessData,
    GetAssemblyRealestateUserAndAccessData_realestateUsersAndAccess
} from "src/api/generated/GetAssemblyRealestateUserAndAccessData";
import { GET_ASSEMBLY_REALESTATEUSER_AND_ACCESS } from "src/api/assembly";

export enum SORT {
    DATE_NEWEST = "dateNewest",
    DATE_OLDEST = "dateOldest"
}

export type FilterStateType = ASSEMBLY_STATE | "All";

export interface FilterSelection {
    label: string;
    id: FilterStateType;
}
export interface IFilter {
    state: FilterStateType;
    sort: SORT;
    query: string;
    types: PROCESS[];
    showMyRealestates: boolean;
}
export interface SearchTag {
    label: string;
    id: string;
    active: boolean;
}

export class AssembliesListStore {
    rootStore: RootStore;
    realestateId: string = "";
    unitId: string = "";

    assemblyFilter: IFilter = {
        state: ASSEMBLY_STATE.PREPARING,
        sort: SORT.DATE_NEWEST,
        query: "",
        types: [],
        showMyRealestates: false
    };

    searchTags: SearchTag[] = [];

    realestateUserData: GetAssemblyRealestateUserAndAccessData_realestateUsersAndAccess[] = [];

    isTabAdditionalClicked: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        this.searchTags = this.getInitialSearchTags();

        makeObservable(this, {
            assemblyFilter: observable,
            searchTags: observable,
            realestateUserData: observable,
            realestateId: observable,
            unitId: observable,
            isTabAdditionalClicked: observable,

            setFilter: action,
            setTag: action,
            clearFilter: action,
            setRealestateUserData: action,
            setRealestateId: action,
            setUnitId: action,
            getRealestateName: action,
            getUnitName: action,
            addSearchTag: action,
            setIsTabAdditionalClicked: action,
            getDate: action,
            getDateWithLongMonth: action,
            updateSearchTags: action,

            filteredAssemblies: computed,
            assemblyStats: computed
        });
    }

    getInitialSearchTags(): SearchTag[] {
        if (
            !this.rootStore.authStore.user?.hasOnlyCoownerRole ||
            !this.rootStore.authStore.user?.hasOnlyCoownerAuditorRole
        ) {
            return [
                {
                    label: i18next.t("screens.tickets.list.my_realestates"),
                    id: "SHOW_MYREALESTATE",
                    active: this.assemblyFilter.showMyRealestates
                }
            ];
        }
        return [];
    }

    updateSearchTags() {
        this.searchTags = this.getInitialSearchTags();
    }

    addSearchTag(label: string, id: string, active: boolean, type?: PROCESS): void {
        const newSearchTag: SearchTag = {
            label,
            id,
            active
        };

        // Push the new search tag to the searchTags array
        this.searchTags.push(newSearchTag);
    }

    setRealestateUserData = async () => {
        if (
            !this.rootStore.authStore.user?.hasOnlyCoownerRole ||
            !this.rootStore.authStore.user?.hasOnlyCoownerAuditorRole
        ) {
            const { data } = await apolloClientInstance.query<
                GetAssemblyRealestateUserAndAccessData,
                GetAssemblyRealestateUserAndAccessData_realestateUsersAndAccess
            >({
                query: GET_ASSEMBLY_REALESTATEUSER_AND_ACCESS
            });

            runInAction(() => {
                this.realestateUserData = data?.realestateUsersAndAccess || [];
            });
        }
    };

    setRealestateId = (realestateId: string) => {
        this.realestateId = realestateId;
    };

    setUnitId = (unitId: string) => {
        this.unitId = unitId;
    };

    setIsTabAdditionalClicked = (isTabAdditionalClicked: boolean) => {
        this.isTabAdditionalClicked = isTabAdditionalClicked;
    };

    getDateStringForHasuraQuery = (date: any): string => {
        let locale: Locale = de;
        if (i18next.language.includes("de")) locale = de;
        if (i18next.language.includes("en")) locale = enGB;
        if (i18next.language.includes("fr")) locale = fr;
        if (i18next.language.includes("it")) locale = it;

        return format(date, "yyyy-MM-dd", { locale });
    };

    getDate = (timestamp: string) => {
        if (timestamp) {
            return format(new Date(timestamp), "dd.MM.yyyy");
        }
    };

    getDateWithLongMonth = (timestamp: string) => {
        if (timestamp) {
            return format(new Date(timestamp), "dd. MMMM yyyy");
        }
    };

    get filteredAssemblies() {
        const filter = this.assemblyFilter;

        return this.rootStore.assemblyStore.assemblies
            .filter((assembly) => {
                if (filter.query === "") {
                    return true;
                }

                // Generate multiple formatted dates for searching
                const formattedDate = assembly.date ? this.getDate(assembly.date) : "";
                const formattedDateWithLongMonth = assembly.date ? this.getDateWithLongMonth(assembly.date) : "";

                // Join realestate names for searching
                const realestateNames = assembly.assemblyrealestates.map((re) => re.realestate.name ?? "").join(" ");

                // Join realestate numbers for searching
                const realestateNumbers = assembly.assemblyrealestates
                    .map((re) => re.realestate.number ?? "")
                    .join(" ");

                const searchString = [
                    assembly.title ?? "",
                    formattedDate,
                    formattedDateWithLongMonth,
                    realestateNames,
                    realestateNumbers,
                    assembly.state ?? "",
                    assembly.city ?? "",
                    assembly.zip ?? "",
                    assembly.street ?? ""
                ]
                    .join("")
                    .toLowerCase();

                return searchString.includes(filter.query.toLowerCase());
            })
            .filter((assembly) => {
                if (filter.showMyRealestates) {
                    return this.realestateUserData.some(
                        (user) => user.realestateid === assembly.assemblyrealestates?.[0]?.realestate?.id
                    );
                }
                return true;
            })
            .sort((lhs, rhs) => {
                const lhsDate = lhs.date ? new Date(lhs.date).getTime() : 0;
                const rhsDate = rhs.date ? new Date(rhs.date).getTime() : 0;

                if (filter.sort === SORT.DATE_OLDEST) {
                    return lhsDate - rhsDate; // Sort from oldest to newest
                } else if (filter.sort === SORT.DATE_NEWEST) {
                    return rhsDate - lhsDate; // Sort from newest to oldest
                } else {
                    return 0;
                }
            });
    }

    get assemblyStats() {
        const allAssemblies = [...this.filteredAssemblies];
        const getLength = (assemblyState: ASSEMBLY_STATE) => {
            return allAssemblies.filter((assembly) => assembly.state === assemblyState)?.length ?? 0;
        };
        return {
            preparing: getLength(ASSEMBLY_STATE.PREPARING),
            datePublished: getLength(ASSEMBLY_STATE.DATE_PUBLISHED),
            agendaPublished: getLength(ASSEMBLY_STATE.AGENDA_PUBLISHED),
            protocolPublished: getLength(ASSEMBLY_STATE.PROTOCOL_PUBLISHED),
            closed: getLength(ASSEMBLY_STATE.CLOSED)
        };
    }

    setFilter = (newFilter: IFilter) => {
        this.assemblyFilter = newFilter;

        this.filteredAssemblies.forEach((assembly) => {
            // this.kanbanBoard[assembly.state].push(assembly.number || 0);
        });
    };

    clearFilter = () => {
        this.searchTags = [...this.searchTags].map((t) => {
            return { ...t, active: false };
        });

        this.setFilter({
            state: ASSEMBLY_STATE.PREPARING,
            sort: SORT.DATE_NEWEST,
            query: "",
            types: [],
            showMyRealestates: false
        });
    };

    setTag = (id: string, active: boolean) => {
        const newSearchTags: SearchTag[] = [...this.searchTags].map((t) => {
            if (t.id === id) {
                return { ...t, active: active };
            }
            return t;
        });

        const activeProcesses: PROCESS[] = [];

        newSearchTags.forEach((tag) => {
            if (tag.active && tag.type) {
                activeProcesses.push(tag.type);
            }
        });

        this.searchTags = newSearchTags;

        this.setFilter({
            ...this.assemblyFilter,
            types: activeProcesses,
            showMyRealestates: newSearchTags.find((st) => st.id === "SHOW_MYREALESTATE")?.active ?? false
        });
    };

    getRealestateName = async () => {
        const { data } = await apolloClientInstance.query<GetRealestateNameById, GetRealestateNameByIdVariables>({
            query: GET_REALESTATENAME_BY_ID,
            variables: {
                realestateid: this.realestateId
            }
        });

        return data.common_realestates.length > 0 ? data.common_realestates[0].name : "";
    };

    getUnitName = async () => {
        const { data } = await apolloClientInstance.query<GetUnitNameById, GetUnitNameByIdVariables>({
            query: GET_UNITNAME_BY_ID,
            variables: {
                unitid: this.unitId
            }
        });

        return data.common_units.length > 0 ? data.common_units[0].name : "";
    };
}
